import React, { FC } from 'react';
import { useLingui } from '@lingui/react/macro';

import HowBrandWorks from '@rover/components/src/HowBrandWorks';
import { Props as SectionProps } from '@rover/kibble/patterns/Section';
import useBrandSpecificPets from '@rover/shared/js/hooks/useBrandSpecificPets';
import getBrandDisplayName from '@rover/shared/js/utils/getBrandDisplayName';
import getBrandSpecificValue from '@rover/shared/js/utils/getBrandSpecificValue';
import { PetType } from '@rover/types/src/Pet';

interface HowBrandWorksSectionProps {
  serviceTypeName?: string;
  providerNounPlural?: string;
  petType?: PetType;
  htmlRef?: React.RefObject<HTMLElement | null>;
  headingVariant?: 'display' | 'heading';
  py?: SectionProps['py'];
  mt?: SectionProps['mt'];
}

interface StepDefinition {
  heading: string;
  content: string;
}

// Pick a pet type for a brand's copy/images: one-pet brands use that pet,
// multiple-pet brands: no-cat use dog(DogBuddy), and brands that also include cats fall back to generic "pets".
const resolveBrandPetType = (brandPets: PetType[]): PetType | undefined => {
  if (brandPets.length === 1) return brandPets[0];
  return brandPets.includes(PetType.CAT) ? undefined : PetType.DOG;
};

const HowBrandWorksSection: FC<HowBrandWorksSectionProps> = ({
  serviceTypeName,
  providerNounPlural,
  petType,
  htmlRef,
  headingVariant = 'display',
  py,
  mt,
}) => {
  const { t } = useLingui();
  const brandDisplayName = getBrandDisplayName();
  const resolvedProviderNoun = providerNounPlural ?? t`sitters`;

  const brandPets = useBrandSpecificPets();
  const resolvedPetType = petType ?? resolveBrandPetType(brandPets);

  let serviceAgnosticFirstStepContent: string;
  let displayHeading: string;
  switch (resolvedPetType) {
    case PetType.DOG:
      serviceAgnosticFirstStepContent = t`Read verified reviews by dog parents like you and choose a sitter who’s a great match for you and your dog.`;
      displayHeading = t`Meet local ${resolvedProviderNoun} who will treat your dogs like family`;
      break;
    case PetType.CAT:
      serviceAgnosticFirstStepContent = t`Read verified reviews by cat parents like you and choose a sitter who’s a great match for you and your cat.`;
      displayHeading = t`Meet local ${resolvedProviderNoun} who will treat your cats like family`;
      break;
    case PetType.PUPPY:
      serviceAgnosticFirstStepContent = t`Read verified reviews by puppy parents like you and choose a sitter who’s a great match for you and your puppy.`;
      displayHeading = t`Meet local ${resolvedProviderNoun} who will treat your puppies like family`;
      break;
    default:
      serviceAgnosticFirstStepContent = t`Read verified reviews by pet parents like you and choose a sitter who’s a great match for you and your pets.`;
      displayHeading = t`Meet local ${resolvedProviderNoun} who will treat your pets like family`;
  }

  const steps = getBrandSpecificValue<StepDefinition[]>({
    rover: [
      {
        heading: t`Search`,
        content: serviceTypeName
          ? t`Browse profiles of ${resolvedProviderNoun} near you who offer ${serviceTypeName}`
          : serviceAgnosticFirstStepContent,
      },
      {
        heading: t`Meet In Person`,
        content: t`Schedule a free in-person Meet & Greet to find the perfect fit`,
      },
      {
        heading: t`Book & Pay`,
        content: t`It's that easy! All payments are processed through ${brandDisplayName}'s secured platform`,
      },
    ],
    catInAFlat: [
      {
        heading: t`Search`,
        content: t`Read verified reviews by pet parents like yourself and choose a sitter who's a great match for you and your pets`,
      },
      {
        heading: t`Book & Pay`,
        content: t`It's that easy! All payments are processed through ${brandDisplayName}'s secured platform`,
      },
      {
        heading: t`Relax`,
        content: t`Enjoy daily photo updates and know your cat is in safe hands while you're away.`,
      },
    ],
  });

  // Only forward spacing overrides that are provided so callers that omit them
  // keep the base Section defaults.
  const sectionSpacing = {
    ...(py !== undefined && { py }),
    ...(mt !== undefined && { mt }),
  };

  return (
    <HowBrandWorks htmlRef={htmlRef} {...sectionSpacing}>
      <HowBrandWorks.Display variant={headingVariant}>{displayHeading}</HowBrandWorks.Display>
      <HowBrandWorks.List petType={resolvedPetType}>
        {steps.map(({ heading, content }, index) => (
          <HowBrandWorks.ListItem key={index}>
            <HowBrandWorks.ItemHeading step={index + 1}>{heading}</HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>{content}</HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
        ))}
      </HowBrandWorks.List>
    </HowBrandWorks>
  );
};

export default HowBrandWorksSection;
